import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import styles from './IssuedLaboratoryRequestsList.module.scss';
import FlatList from '../../../Base/Components/FlatList';
import IssuedLaboratoryRequestsListItem from './IssuedLaboratoryRequestsListItem';
import { useFlatDataList } from '../../../Base/Hooks/useFlatDataList';
import {
	LaboratoryRequisitionResult,
	PaginationOrderHow,
	useIssuedLaboratoryRequestsQuery,
} from '../../../../graphql/graphql';

export type IssuedLaboratoryRequestsListHandle = {
	refresh: () => void;
};

interface Props {
	patientId: string;
	workplaceId: string;
}

const wrapperId = 'laboratoryRequestsWrapper';

const IssuedLaboratoryRequestsList = forwardRef<
	IssuedLaboratoryRequestsListHandle,
	Props
>(({ patientId, workplaceId }, ref) => {
	const { flatData, pagination, getNextPage, appendData, updateItem, reset } =
		useFlatDataList<LaboratoryRequisitionResult>();

	const { data, isLoading, refetch, isFetching } =
		useIssuedLaboratoryRequestsQuery({
			where: { userId: patientId, workplaceId },
			pagination: {
				...pagination,
				order: { key: 'created_at', how: PaginationOrderHow.Desc },
			},
		});

	useEffect(() => {
		appendData(
			(data?.laboratoryRequisitionResults
				?.data as LaboratoryRequisitionResult[]) ?? [],
		);
	}, [data]);

	const refresh = () => {
		reset();
		refetch();
	};

	useImperativeHandle(ref, () => ({ refresh }), [refresh]);

	return (
		<div className={styles.wrapper}>
			<ScrollContainer wrapperId={wrapperId} className={styles.list}>
				<FlatList
					onRefresh={refetch}
					onLoadMore={getNextPage}
					data={flatData}
					isLoading={isLoading || isFetching}
					hasMoreData={
						data?.laboratoryRequisitionResults?.meta?.hasNextPage || false
					}
					scrollableTargetId={wrapperId}
					renderItem={item => (
						<IssuedLaboratoryRequestsListItem
							item={item}
							patientId={patientId}
							onUpdate={(item: LaboratoryRequisitionResult) => updateItem(item)}
							refreshList={refresh}
						/>
					)}
				/>
			</ScrollContainer>
		</div>
	);
});

export default IssuedLaboratoryRequestsList;
